import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Navbar } from "components/layout/components/Navbar";
import folderIcon from "../assets/images/FolderIcon.svg";
import { Link } from "react-router-dom";
import { routePath } from "config/routeMgt/routePath";
import { useState } from "react";
import { CreateFolder } from "../components/CreateFolder";
import { useGetFolders } from "../hooks/useGetFolders";
import { usePagination } from "hooks/usePagination";
import { ScaleLoader } from "react-spinners";
import { Button, Dropdown, Menu, Pagination, Popconfirm } from "antd";
import { useDelete } from "hooks/useDelete";
import { QUERY_KEY_FOR_FOLDERS } from "../hooks/useGetFolders";
import { EditFolder } from "../components/EditFolder";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { getSubscriptionRulesStatus } from "features/home/hooks/useGetSubscriptionRulesStatus";
import { openNotification } from "utils/notification";

const deleteEndPointUrl = "delete-folder/";
export const Folders = () => {
  const [newFolder, setNewFolder] = useState(false);
  const [editFolder, setEditFolder] = useState(false);
  const [folderId, setFolderId] = useState<number>();
  const [checkingSubscription, setCheckingSubscription] = useState(false);

  const { pagination, onChange } = usePagination();
  const { data, isLoading } = useGetFolders({ pagination });
  const { userInfo, token } = useGetUserInfo();

  const { removeData } = useDelete({
    queryKey: QUERY_KEY_FOR_FOLDERS,
    deleteEndPointUrl,
  });
  const addFolder = () => {
    setNewFolder(true);
  };

  const openCreateFolderModal = () => setNewFolder(true);

  const handleCreateFolderClick = async () => {
    if (!token) {
      openNotification({
        title: "You must be signed in to create a folder.",
        description: "Please log in and try again.",
        state: "error",
        duration: 4.5,
      });
      return;
    }

    setCheckingSubscription(true);
    try {
      const subscriptionRulesStatus = await getSubscriptionRulesStatus({
        token,
        label: "create_folder",
      });

      const canCreateFolderBySubscription =
        subscriptionRulesStatus === undefined ||
        subscriptionRulesStatus.rules[0].status === true;

      if (canCreateFolderBySubscription) {
        openCreateFolderModal();
        return;
      }

      openNotification({
        title: "You do not have enough credits to create a folder.",
        description: "Please upgrade your plan to create a folder.",
        state: "error",
        duration: 4.5,
      });
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { message?: string } };
      };
      const message =
        axiosErr.response?.data?.message ??
        (err instanceof Error ? err.message : "Unknown error occurred.");

      openNotification({
        title: "Could not check folder creation eligibility.",
        description: message,
        state: "error",
        duration: 4.5,
      });
    } finally {
      setCheckingSubscription(false);
    }
  };
  const handleFolder = (id: number) => {
    setFolderId(id);
    setEditFolder(true);
  };

  // Function to format the creation date
  const formatCreationDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <Navbar title="Folders" description="Create and manage folders" />
      <CreateFolder
        open={newFolder}
        handleClose={() => setNewFolder(false)}
      />
      <EditFolder
        id={folderId}
        open={editFolder}
        handleClose={() => setEditFolder(false)}
      />
      <div className="flex justify-end">
        <Button
          data-tour="create-folder"
          className="button flex items-center gap-x-2"
          onClick={() => void handleCreateFolderClick()}
        >
          <i className="ri-checkbox-blank-circle-fill text-xs"></i>
          <span>Create Folder</span>
        </Button>
      </div>

      <div className="flex justify-center mt-8">
        {isLoading ? <ScaleLoader color="var(--app-color-primary)" /> : null}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {data?.data.map((item) => (

          <div
            key={item.id}
            data-tour="folder-item"
            className="flex items-center justify-between gap-x-3 bg-white border rounded-md p-3 hover:border-b hover:border-primary transition-all ease-in-out duration-300"
          >
            <div className="flex items-center gap-x-2">
              <Link to={routePath.folderDetails(item.id).path}>
                <img
                  src={folderIcon}
                  alt="folder logo"
                  className="md:h-11 h-5 hover:border border-gray-600 rounded-full"
                />
              </Link>
              <div className="flex flex-col">
                <Link
                  to={routePath.folderDetails(item.id).path}
                  className="text-primary text-sm font-medium capitalize hover:text-black"
                >
                  {item.name}
                </Link>
                {item?.owner && (
                  <p className="flex text-xs text-gray-500 items-center">
                    <i className="ri-user-fill text-xs"></i> 
                    {userInfo?.recruiter.id == item.owner.id 
                      ? 'You' 
                      : item.owner.first_name + ' ' + item.owner.last_name}
                  </p>
                )}
                {/* Added creation date here */}
                {item.created_at && (
                  <p className="text-xs text-gray-500 mt-1">
                    <i className="ri-calendar-line text-xs mr-1"></i>
                    Created {formatCreationDate(item.created_at)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center">
              <Dropdown
                trigger={["click"]}
                overlay={
                  <Menu>
                    <Menu.Item key="1">
                      <Link to={routePath.folderDetails(item.id).path}>View</Link>
                    </Menu.Item>
                    <Menu.Item
                      key="2"
                      onClick={() => handleFolder(item.id as unknown as number)}
                    >
                      Edit
                    </Menu.Item>
                    <Menu.Item key="3">
                      <Popconfirm
                        okButtonProps={{
                          style: { backgroundColor: '#f5222d', color: '#ffffff' }, // Custom styles
                        }}
                        title="Delete folder"
                        description="Are you sure to delete this folder?"
                        onConfirm={() => removeData(item.id as unknown as number)}
                        okText="Yes delete"
                        cancelText="Cancel"
                      >
                        Delete
                      </Popconfirm>
                    </Menu.Item>
                  </Menu>
                }
              >
                <i className="ri-more-2-fill text-lg font-semibold hover:text-black cursor-pointer" data-tour="folder-actions"></i>
              </Dropdown>
            </div>
          </div>
        ))}
      </div>

      <div className={isLoading ? "hidden" : "flex justify-end mt-3"}>
        <Pagination
          total={data?.total}
          onChange={onChange}
          current={pagination.current}
        />
      </div>
    </DashboardLayout>
  );
};